import React, { useRef, useEffect, useState } from 'react'
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react'
import type { Video } from '../types'
import { api, buildApiUrl } from '../api/client'

interface VideoPlayerProps {
  video: Video
  currentTime: number
  isPlaying: boolean
  onTimeUpdate: (time: number) => void
  onPlayStateChange: (playing: boolean) => void
  onDurationChange?: (duration: number) => void
}

// Extract YouTube video ID from URL
function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
    /^([a-zA-Z0-9_-]{11})$/
  ]
  for (const p of patterns) {
    const m = url.match(p)
    if (m) return m[1]
  }
  return null
}

// Load YouTube IFrame API once
let ytApiReady = false
let ytApiCallbacks: (() => void)[] = []

function ensureYTApi(cb: () => void) {
  if (ytApiReady) { cb(); return }
  ytApiCallbacks.push(cb)
  if (ytApiCallbacks.length === 1) {
    const tag = document.createElement('script')
    tag.src = 'https://www.youtube.com/iframe_api'
    document.head.appendChild(tag)
    ;(window as any).onYouTubeIframeAPIReady = () => {
      ytApiReady = true
      ytApiCallbacks.forEach(fn => fn())
      ytApiCallbacks = []
    }
  }
}

export function VideoPlayer({ 
  video, 
  currentTime, 
  isPlaying, 
  onTimeUpdate, 
  onPlayStateChange,
  onDurationChange 
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const ytContainerRef = useRef<HTMLDivElement>(null)
  const ytPlayerRef = useRef<any>(null)
  const ytIntervalRef = useRef<number | null>(null)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [videoSrc, setVideoSrc] = useState<string>('')
  const [isLoadingUrl, setIsLoadingUrl] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [cropPreview, setCropPreview] = useState<'off' | '9:16' | '1:1'>('off')

  const youtubeId = extractYouTubeId(video.url)
  const isYouTube = !!youtubeId

  // --- YouTube IFrame Player ---
  useEffect(() => {
    if (!isYouTube || !youtubeId) return

    let player: any = null

    ensureYTApi(() => {
      if (!ytContainerRef.current) return
      // Create a div for the player inside the container
      const el = document.createElement('div')
      el.id = `yt-player-${youtubeId}`
      ytContainerRef.current!.innerHTML = ''
      ytContainerRef.current!.appendChild(el)

      player = new (window as any).YT.Player(el.id, {
        videoId: youtubeId,
        playerVars: {
          autoplay: 0,
          controls: 0,          // We render our own controls
          modestbranding: 1,
          rel: 0,
          enablejsapi: 1,
          origin: window.location.origin,
          playsinline: 1,
        },
        events: {
          onReady: (e: any) => {
            ytPlayerRef.current = e.target
            const dur = e.target.getDuration()
            if (dur) {
              setDuration(dur)
              onDurationChange?.(dur)
            }
            e.target.setVolume(volume * 100)
          },
          onStateChange: (e: any) => {
            const YT = (window as any).YT
            if (e.data === YT.PlayerState.PLAYING) {
              onPlayStateChange(true)
            } else if (e.data === YT.PlayerState.PAUSED || e.data === YT.PlayerState.ENDED) {
              onPlayStateChange(false)
            }
          }
        }
      })
    })

    return () => {
      if (ytIntervalRef.current) clearInterval(ytIntervalRef.current)
      if (ytPlayerRef.current?.destroy) {
        try { ytPlayerRef.current.destroy() } catch (_) {}
      }
      ytPlayerRef.current = null
    }
  }, [youtubeId])

  // YouTube: time update polling
  useEffect(() => {
    if (!isYouTube) return
    if (ytIntervalRef.current) clearInterval(ytIntervalRef.current)

    ytIntervalRef.current = window.setInterval(() => {
      const p = ytPlayerRef.current
      if (p?.getCurrentTime) {
        onTimeUpdate(p.getCurrentTime())
      }
    }, 250)

    return () => {
      if (ytIntervalRef.current) clearInterval(ytIntervalRef.current)
    }
  }, [isYouTube, onTimeUpdate])

  // YouTube: play/pause sync
  useEffect(() => {
    const p = ytPlayerRef.current
    if (!p || !isYouTube) return
    try {
      if (isPlaying) { p.playVideo() } else { p.pauseVideo() }
    } catch (_) {}
  }, [isPlaying, isYouTube])

  // YouTube: seek sync
  useEffect(() => {
    const p = ytPlayerRef.current
    if (!p || !isYouTube) return
    try {
      const ytTime = p.getCurrentTime?.() ?? 0
      if (Math.abs(ytTime - currentTime) > 1) {
        p.seekTo(currentTime, true)
      }
    } catch (_) {}
  }, [currentTime, isYouTube])

  // YouTube: volume sync
  useEffect(() => {
    const p = ytPlayerRef.current
    if (!p || !isYouTube) return
    try {
      if (isMuted) { p.mute() } else { p.unMute(); p.setVolume(volume * 100) }
    } catch (_) {}
  }, [volume, isMuted, isYouTube])

  const isProbablyNonPlayableUrl = (url: string) => {
    if (!url) return true
    const lower = url.toLowerCase()
    if (lower.startsWith('dropbox://')) return true
    if (lower.startsWith('cloud://')) return true
    if (lower.startsWith('file://')) return true
    if (lower.startsWith('upload://')) return true
    // YouTube handled by iframe — don't flag as non-playable
    return false
  }

  // Fetch presigned URL for MinIO/S3 videos, with HTTPS check
  useEffect(() => {
    if (isYouTube) return  // YouTube handled by iframe player above

    const fetchVideoUrl = async () => {
      setLoadError(null)
      const isMinioUrl = video.url.startsWith('minio://')
      const isS3Url = video.url.startsWith('s3://')

      if (isMinioUrl || isS3Url) {
        setIsLoadingUrl(true)
        const minioPath = isMinioUrl
          ? video.url.replace('minio://', '')
          : video.url.replace('s3://', '')

        try {
          // Step 1: Get a signed stream token via axios (sends session cookies).
          // axios withCredentials works fine for same-origin XHR in iframes.
          // Only the <video src=""> tag doesn't send cookies, which is why
          // we pass the token as a query parameter instead.
          const tokenRes = await api.get('/studio/stream-token', {
            params: { path: minioPath }
          })
          const token = tokenRes.data?.token
          if (!token) throw new Error('No token in response')

          // Step 2: Build stream URL with token for the <video> element
          const streamUrl = `${buildApiUrl('/studio/stream')}?path=${encodeURIComponent(minioPath)}&token=${encodeURIComponent(token)}`
          console.log('Video stream URL (token-auth):', streamUrl)
          setVideoSrc(streamUrl)
        } catch (err) {
          console.error('Failed to get stream token, check session auth:', err)
          setLoadError('Failed to authenticate video stream')
        } finally {
          setIsLoadingUrl(false)
        }
      } else {
        setVideoSrc(video.url)
      }
    }
    
    fetchVideoUrl()
  }, [video.url])

  const showPreviewPlaceholder = !isYouTube && (isLoadingUrl || !videoSrc || loadError || isProbablyNonPlayableUrl(videoSrc))

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const updateTime = () => onTimeUpdate(video.currentTime)
    const updateDuration = () => {
      setDuration(video.duration)
      if (video.duration > 0) onDurationChange?.(video.duration)
    }
    const handleEnded = () => onPlayStateChange(false)

    video.addEventListener('timeupdate', updateTime)
    video.addEventListener('loadedmetadata', updateDuration)
    video.addEventListener('ended', handleEnded)
    // Metadata may have arrived before these listeners did.
    if (video.readyState >= 1) updateDuration()

    return () => {
      video.removeEventListener('timeupdate', updateTime)
      video.removeEventListener('loadedmetadata', updateDuration)
      video.removeEventListener('ended', handleEnded)
    }
    // The <video> only mounts once the placeholder goes away (the source URL resolves after the
    // first render), so re-run then. With only the callbacks as deps this ran once against a null
    // ref and never again: the clock, playhead and live captions froze at the first seek.
  }, [onTimeUpdate, onPlayStateChange, showPreviewPlaceholder, videoSrc])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    if (isPlaying) {
      video.play()
    } else {
      video.pause()
    }
  }, [isPlaying])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // Only seek when user/external code sets a time significantly different
    // from where the video is currently playing (avoids feedback loop with timeupdate)
    if (Math.abs(video.currentTime - currentTime) > 0.5) {
      video.currentTime = currentTime
    }
  }, [currentTime])

  const togglePlay = () => {
    onPlayStateChange(!isPlaying)
  }

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume)
    setIsMuted(newVolume === 0)

    if (isYouTube) {
      const p = ytPlayerRef.current
      if (p) { try { p.setVolume(newVolume * 100); if (newVolume > 0) p.unMute(); else p.mute() } catch (_) {} }
    } else {
      const v = videoRef.current
      if (v) v.volume = newVolume
    }
  }

  const toggleMute = () => {
    if (isMuted) {
      setIsMuted(false)
      if (isYouTube) { try { ytPlayerRef.current?.unMute(); ytPlayerRef.current?.setVolume(volume * 100) } catch (_) {} }
      else if (videoRef.current) videoRef.current.volume = volume
    } else {
      setIsMuted(true)
      if (isYouTube) { try { ytPlayerRef.current?.mute() } catch (_) {} }
      else if (videoRef.current) videoRef.current.volume = 0
    }
  }

  const toggleFullscreen = () => {
    const container = isYouTube ? ytContainerRef.current?.parentElement : videoRef.current?.parentElement
    if (!container) return

    if (!isFullscreen) {
      container.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  // Show placeholder while fetching URL or when video can't be previewed
  if (showPreviewPlaceholder) {
    return (
      <div className="video-player-container bg-black rounded-lg overflow-hidden h-full min-h-[300px] flex items-center justify-center relative">
        {video.thumbnail ? (
          <img
            src={video.thumbnail}
            alt={video.title}
            className="absolute inset-0 w-full h-full object-contain opacity-80"
          />
        ) : null}
        <div className="relative z-10 bg-black/60 border border-gray-700 rounded-lg px-4 py-3 max-w-[90%] text-center">
          <div className="text-sm text-white font-medium">Preview unavailable</div>
          <div className="text-xs text-gray-300 mt-1">
            {isLoadingUrl ? 'Loading video…' : (loadError ? 'Video failed to load.' : 'This source URL is not a direct video stream.')}
          </div>
          <div className="text-xs text-gray-400 mt-2">
            Import a video (or use a cached MinIO video) for live playback.
          </div>
        </div>
      </div>
    )
  }

  // YouTube IFrame player
  if (isYouTube) {
    return (
      <div className="video-player-container bg-black rounded-lg overflow-hidden h-full flex flex-col">
        <div
          ref={ytContainerRef}
          className="w-full flex-1 bg-black [&>div]:w-full [&>div]:h-full [&>iframe]:w-full [&>iframe]:h-full"
        />

        {/* Custom Controls (same as HTML5 player) */}
        <div className="video-controls">
          <button onClick={togglePlay} className="control-button" aria-label={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>
          <span className="text-sm text-white">
            {formatTime(currentTime)} / {formatTime(duration || video.duration || 0)}
          </span>
          <div className="flex-1 mx-4">
            <input
              type="range"
              min="0"
              max={duration || video.duration || 1}
              value={currentTime}
              onChange={(e) => onTimeUpdate(Number(e.target.value))}
              className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #4f46e5 0%, #4f46e5 ${(currentTime / (duration || video.duration || 1)) * 100}%, #374151 ${(currentTime / (duration || video.duration || 1)) * 100}%, #374151 100%)`
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleMute} className="control-button" aria-label={isMuted ? 'Unmute' : 'Mute'}>
              {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
            <input
              type="range" min="0" max="1" step="0.1"
              value={isMuted ? 0 : volume}
              onChange={(e) => handleVolumeChange(Number(e.target.value))}
              className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
          </div>
          <button onClick={toggleFullscreen} className="control-button" aria-label="Fullscreen">
            <Maximize size={20} />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="video-player-container bg-black rounded-lg overflow-hidden h-full flex flex-col">
      <div className="relative flex-1 min-h-0 bg-black">
        <video
          ref={videoRef}
          className="w-full h-full object-contain bg-black"
          src={videoSrc}
          poster={video.thumbnail}
          preload="auto"
          onError={(e) => {
            const v = e.currentTarget as HTMLVideoElement
            const mediaErr = v.error
            console.error('Video load error:', { src: videoSrc, code: mediaErr?.code, message: mediaErr?.message })
            setLoadError(mediaErr?.message || 'error')
            onPlayStateChange(false)
          }}
        />
        {/* Crop-frame preview: shows what a center-crop to the chosen aspect
            keeps; the box-shadow darkens everything that would be cut. */}
        {cropPreview !== 'off' && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div
              style={{
                height: '100%',
                aspectRatio: cropPreview === '1:1' ? '1 / 1' : '9 / 16',
                maxWidth: '100%',
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.58)',
                outline: '2px solid rgba(99,102,241,0.9)',
                outlineOffset: '-2px',
                borderRadius: 2,
              }}
            />
            <span className="absolute top-2 left-2 text-[11px] font-semibold text-white bg-indigo-600/80 px-2 py-0.5 rounded">
              {cropPreview} crop preview
            </span>
          </div>
        )}
        {/* Toggle */}
        <div className="absolute top-2 right-2 flex gap-1 bg-black/50 rounded-lg p-0.5 backdrop-blur-sm">
          {(['off', '9:16', '1:1'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setCropPreview(v)}
              className={`text-[11px] px-2 py-1 rounded-md transition-colors ${
                cropPreview === v ? 'bg-indigo-600 text-white' : 'text-gray-300 hover:text-white'
              }`}
              title={v === 'off' ? 'No crop guide' : `Preview ${v} center-crop`}
            >
              {v === 'off' ? 'Full' : v}
            </button>
          ))}
        </div>
      </div>
      
      {/* Custom Controls */}
      <div className="video-controls">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlay}
          className="control-button"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause size={20} /> : <Play size={20} />}
        </button>

        {/* Time Display — one line; on a phone it wrapped to three and covered the picture */}
        <span className="text-xs sm:text-sm text-white whitespace-nowrap shrink-0">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>

        {/* Progress Bar */}
        <div className="flex-1 min-w-0 mx-2 sm:mx-4">
          <input
            type="range"
            min="0"
            max={duration}
            value={currentTime}
            onChange={(e) => onTimeUpdate(Number(e.target.value))}
            className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #4f46e5 0%, #4f46e5 ${(currentTime / duration) * 100}%, #374151 ${(currentTime / duration) * 100}%, #374151 100%)`
            }}
          />
        </div>

        {/* Volume Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleMute}
            className="control-button"
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>
          
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={isMuted ? 0 : volume}
            onChange={(e) => handleVolumeChange(Number(e.target.value))}
            className="hidden sm:block w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        {/* Fullscreen Button */}
        <button
          onClick={toggleFullscreen}
          className="control-button"
          aria-label="Fullscreen"
        >
          <Maximize size={20} />
        </button>
      </div>
    </div>
  )
}
